import { useState } from 'react';
import { Loader2, TrendingDown, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Collapsible({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium hover:underline w-full text-left"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
        {badge}
      </button>
      {open && <div className="mt-2 ml-6">{children}</div>}
    </div>
  );
}

function Kv({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground min-w-[180px]">{label}:</span>
      <span className={mono ? 'font-mono' : ''}>{value ?? 'n/a'}</span>
    </div>
  );
}

function fmt(v) {
  if (v == null) return 'n/a';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function RevenueDeltaDiagnosticPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [entityId, setEntityId] = useState('');
  const [hoursBack, setHoursBack] = useState(24);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const params = { hours_back: hoursBack };
      if (entityId) params.entity_id = entityId;
      const { data } = await diagnosticsAPI.revenueDelta(params);
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-amber-600" />
          Revenue Delta Attribution
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only diagnostic attributing an overnight revenue move. Checks FX revaluation,
          booking modifications, deploy correlation, and forecast-to-actual flips.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Entity (optional)</label>
            <input
              type="text"
              className="border rounded px-2 py-1 text-sm w-32"
              placeholder="e.g. afpv"
              value={entityId}
              onChange={e => setEntityId(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Hours back</label>
            <input
              type="number"
              className="border rounded px-2 py-1 text-sm w-20"
              value={hoursBack}
              onChange={e => setHoursBack(parseInt(e.target.value) || 24)}
            />
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run Diagnostic
          </Button>
        </div>

        {r && (
          <div className="space-y-4">
            {/* Header */}
            <div className="bg-muted/50 rounded p-3 text-sm space-y-1">
              <Kv label="Checked at" value={r.checked_at} />
              <Kv label="Month" value={`${r.year}-${String(r.month).padStart(2, '0')}`} />
              <Kv label="Entity" value={r.entity_id || '(all entities)'} />
              <Kv label="Base currency" value={r.base_currency} />
            </div>

            {/* Q1: Figure definition */}
            <Collapsible title="Q1: Figure Definition" defaultOpen>
              <div className="text-sm space-y-2">
                <p className="text-muted-foreground">{r.figure_definition?.description}</p>
                <div className="text-xs text-muted-foreground space-y-0.5">
                  {r.figure_definition?.file_refs?.map((ref, i) => (
                    <div key={i} className="font-mono">{ref}</div>
                  ))}
                </div>
              </div>
            </Collapsible>

            {/* Q2: Count vs amount */}
            <Collapsible
              title="Q2: Current Counts and Amounts"
              badge={
                <Badge variant="outline" className="text-xs ml-2">
                  {r.count_summary?.total_bookings} bookings
                </Badge>
              }
              defaultOpen
            >
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <Kv label="Uplisting bookings" value={r.count_summary?.uplisting} mono />
                  <Kv label="Smoobu bookings" value={r.count_summary?.smoobu} mono />
                  <Kv label="BR OS bookings" value={r.count_summary?.bros} mono />
                  <Kv label="Total bookings" value={r.count_summary?.total_bookings} mono />
                  <Kv label="Paid invoices (revenue)" value={r.count_summary?.invoices_paid_revenue} mono />
                  <Kv label="Revenue transactions" value={r.count_summary?.transactions_revenue} mono />
                </div>
                <hr />
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <Kv label="Invoice revenue" value={fmt(r.amount_summary?.invoice_revenue)} mono />
                  <Kv label="Transaction revenue" value={fmt(r.amount_summary?.transaction_revenue)} mono />
                  <Kv label="Uplisting entity rev" value={fmt(r.amount_summary?.uplisting_entity_revenue)} mono />
                  <Kv label="Smoobu entity rev" value={fmt(r.amount_summary?.smoobu_entity_revenue)} mono />
                  <Kv label="BR OS entity rev" value={fmt(r.amount_summary?.bros_entity_revenue)} mono />
                  <Kv label="Booking entity rev" value={fmt(r.amount_summary?.booking_entity_revenue)} mono />
                </div>
                <div className="font-semibold">
                  <Kv label="Total revenue" value={fmt(r.amount_summary?.total_revenue)} mono />
                </div>
              </div>
            </Collapsible>

            {/* Q3: Snapshot */}
            <Collapsible title="Q3: Prior Snapshot">
              <div className="text-sm">
                <Badge variant={r.snapshot_info?.has_daily_snapshot ? 'default' : 'secondary'} className="text-xs mb-2">
                  {r.snapshot_info?.has_daily_snapshot ? 'Snapshot exists' : 'No snapshot'}
                </Badge>
                <p className="text-muted-foreground">{r.snapshot_info?.note}</p>
              </div>
            </Collapsible>

            {/* Q4: Modifications */}
            <Collapsible
              title="Q4: Modified Overnight"
              badge={
                <Badge
                  variant={r.modification_summary?.modified_count > 0 ? 'destructive' : 'secondary'}
                  className="text-xs ml-2"
                >
                  {r.modification_summary?.modified_count} modified
                </Badge>
              }
              defaultOpen={r.modification_summary?.modified_count > 0}
            >
              <div className="text-sm space-y-2">
                <Kv label="Hours back" value={r.modification_summary?.hours_back} />
                <Kv label="Cutoff" value={r.modification_summary?.cutoff_iso} />
                <Kv label="Modified (Uplisting)" value={r.modification_summary?.modified_uplisting} mono />
                <Kv label="Modified (Smoobu)" value={r.modification_summary?.modified_smoobu} mono />
                <Kv label="Modified (BR OS)" value={r.modification_summary?.modified_bros} mono />
                <Kv label="Modified set entity rev" value={fmt(r.modification_summary?.modified_entity_revenue_base)} mono />
                <p className="text-xs text-muted-foreground">{r.modification_summary?.note}</p>

                {r.modification_summary?.details?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-1 pr-2">ID</th>
                          <th className="py-1 pr-2">Platform</th>
                          <th className="py-1 pr-2">Property</th>
                          <th className="py-1 pr-2">Guest</th>
                          <th className="py-1 pr-2">Check-in</th>
                          <th className="py-1 pr-2">Status</th>
                          <th className="py-1 pr-2">Type</th>
                          <th className="py-1 pr-2 text-right">Total</th>
                          <th className="py-1 pr-2 text-right">Entity Rev</th>
                          <th className="py-1 pr-2">Synced</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.modification_summary.details.map((d, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            <td className="py-1 pr-2 font-mono">{d.booking_id}</td>
                            <td className="py-1 pr-2">{d.platform}</td>
                            <td className="py-1 pr-2 max-w-[140px] truncate">{d.property}</td>
                            <td className="py-1 pr-2 max-w-[100px] truncate">{d.guest}</td>
                            <td className="py-1 pr-2">{d.check_in}</td>
                            <td className="py-1 pr-2">
                              <Badge variant={d.status === 'cancelled' ? 'destructive' : 'outline'} className="text-xs">
                                {d.status}
                              </Badge>
                            </td>
                            <td className="py-1 pr-2">{d.management_type}</td>
                            <td className="py-1 pr-2 text-right font-mono">{fmt(d.total_price)}</td>
                            <td className="py-1 pr-2 text-right font-mono">{fmt(d.entity_revenue_base)}</td>
                            <td className="py-1 pr-2 text-xs">{d.synced_at?.slice(0, 16)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Collapsible>

            {/* Q5: FX */}
            <Collapsible
              title="Q5: FX Revaluation"
              badge={
                <Badge
                  variant={r.fx_summary?.currencies_in_use?.length > 0 ? 'outline' : 'secondary'}
                  className="text-xs ml-2"
                >
                  {r.fx_summary?.currencies_in_use?.length || 0} cross-currency
                </Badge>
              }
            >
              <div className="text-sm space-y-2">
                <p className="text-muted-foreground text-xs">{r.fx_summary?.note}</p>
                {r.fx_summary?.rates?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-1 pr-3">Currency</th>
                          <th className="py-1 pr-3 text-right">Yesterday rate</th>
                          <th className="py-1 pr-3 text-right">Today rate</th>
                          <th className="py-1 pr-3 text-right">Rate delta</th>
                          <th className="py-1 pr-3 text-right">Exposed native</th>
                          <th className="py-1 pr-3 text-right">Revenue delta ({r.base_currency})</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.fx_summary.rates.map((fx, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            <td className="py-1 pr-3 font-mono">{fx.currency}</td>
                            <td className="py-1 pr-3 text-right font-mono">{fmt(fx.yesterday_rate)}</td>
                            <td className="py-1 pr-3 text-right font-mono">{fmt(fx.today_rate)}</td>
                            <td className="py-1 pr-3 text-right font-mono">{fmt(fx.rate_delta)}</td>
                            <td className="py-1 pr-3 text-right font-mono">{fmt(fx.native_amount_exposed)}</td>
                            <td className="py-1 pr-3 text-right font-mono">{fmt(fx.fx_revenue_delta_base)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Kv label="Total FX delta" value={fmt(r.fx_summary?.total_fx_delta_base)} mono />
              </div>
            </Collapsible>

            {/* Q6: Deploy correlation */}
            <Collapsible title="Q6: Deploy / Reconciliation Correlation">
              <div className="text-sm space-y-2">
                <Kv label="Recent recon actions" value={r.deploy_correlation?.recent_recon_actions_count} mono />
                <Kv label="June recon invoices" value={r.deploy_correlation?.june_recon_invoices?.length} mono />
                <Kv label="Revenue depends on recon?" value={r.deploy_correlation?.revenue_depends_on_reconciliation ? 'YES' : 'No'} />
                <div className="text-xs font-mono text-muted-foreground">{r.deploy_correlation?.file_ref}</div>
                <p className="text-xs text-muted-foreground">{r.deploy_correlation?.note}</p>
                {r.deploy_correlation?.june_recon_invoices?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-1 pr-2">Invoice ID</th>
                          <th className="py-1 pr-2">Type</th>
                          <th className="py-1 pr-2">Payment</th>
                          <th className="py-1 pr-2">Review</th>
                          <th className="py-1 pr-2 text-right">Amount</th>
                          <th className="py-1 pr-2">Applied at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.deploy_correlation.june_recon_invoices.map((inv, i) => (
                          <tr key={i} className="border-b last:border-b-0">
                            <td className="py-1 pr-2 font-mono">{inv.invoice_id}</td>
                            <td className="py-1 pr-2">{inv.invoice_type}</td>
                            <td className="py-1 pr-2">{inv.payment_status}</td>
                            <td className="py-1 pr-2">{inv.review_status}</td>
                            <td className="py-1 pr-2 text-right font-mono">{fmt(inv.amount)}</td>
                            <td className="py-1 pr-2 text-xs">{inv.applied_at?.slice(0, 16)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Collapsible>

            {/* Q7: Forecast-to-actual */}
            <Collapsible title="Q7: Forecast-to-Actual Flip">
              <div className="text-sm space-y-1">
                <Kv label="Actuals cutoff" value={r.forecast_flip?.actuals_cutoff} mono />
                <Kv label="Contribution" value={fmt(r.forecast_flip?.contribution_base)} mono />
                <p className="text-xs text-muted-foreground">{r.forecast_flip?.note}</p>
              </div>
            </Collapsible>

            {/* Q8: Residual */}
            <Collapsible title="Q8: Residual / Attribution Summary" defaultOpen>
              <div className="text-sm space-y-2">
                <div className="bg-muted/50 rounded p-3 space-y-1">
                  <Kv label="Operator prior (~)" value={r.residual?.operator_reported_prior} />
                  <Kv label="Operator current (~)" value={r.residual?.operator_reported_current} />
                  <Kv label="Operator delta (~)" value={r.residual?.operator_reported_delta} />
                </div>
                <hr />
                <Kv label="Computed revenue now" value={fmt(r.residual?.current_computed_revenue)} mono />
                <Kv label="FX contribution" value={fmt(r.residual?.fx_contribution)} mono />
                <Kv label="Modified set entity rev" value={fmt(r.residual?.modification_set_revenue)} mono />
                <Kv label="Recon contribution" value={fmt(r.residual?.recon_contribution)} mono />
                <Kv label="Forecast flip" value={fmt(r.residual?.forecast_flip_contribution)} mono />
                <p className="text-xs text-muted-foreground mt-2">{r.residual?.note}</p>
              </div>
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
